import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Trash2, Users } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { api, errMessage } from "../lib/api";

// Family sharing: invite a co-guardian, list members, join with a code.
export const FamilyPanel = () => {
  const [data, setData] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteResult, setInviteResult] = useState(null);
  const [joinCode, setJoinCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/family/members").then(({ data: d }) => setData(d)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: res } = await api.post("/family/invites", { email: inviteEmail });
      setInviteResult(res);
      setInviteEmail("");
      load();
      toast.success(res.email_sent ? "Invitation emailed" : "Invite created — share the code yourself");
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const join = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/family/redeem", { code: joinCode.trim() });
      toast.success("Welcome to the family! Reloading…");
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (memberId) => {
    try {
      await api.delete(`/family/members/${memberId}`);
      toast.success("Member removed from the family");
      load();
    } catch (err) {
      toast.error(errMessage(err));
    }
  };

  if (!data) return null;
  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="family-panel">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-secondary" aria-hidden="true" />
        <p className="text-sm font-bold">Family members</p>
      </div>
      <div className="space-y-2">
        {data.members.map((m) => (
          <div key={m.user_id} className="flex items-center justify-between gap-2" data-testid={`family-member-${m.user_id}`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{m.name}{m.user_id === data.me ? " (you)" : ""}</p>
              <p className="text-xs text-muted-foreground truncate">{m.email}</p>
            </div>
            {data.i_am_owner && !m.is_owner && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="sm" className="rounded-full text-destructive shrink-0 h-8"
                    data-testid={`family-remove-${m.user_id}`}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Remove {m.name} from the family?</AlertDialogTitle>
                    <AlertDialogDescription>
                      They will immediately lose access to the children's dashboards and stories.
                      You can always invite them again later.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="family-remove-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(m.user_id)}
                      className="bg-destructive hover:bg-destructive/90" data-testid="family-remove-confirm">
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        ))}
      </div>
      <form onSubmit={invite} className="flex gap-2">
        <Input type="email" required placeholder="grandparent@example.com" value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)} className="h-9 text-sm" data-testid="family-invite-email-input" />
        <Button type="submit" disabled={busy} size="sm" className="rounded-lg shrink-0 h-9" data-testid="family-invite-button">
          Invite
        </Button>
      </form>
      {inviteResult && (
        <div className="rounded-xl bg-accent p-3 space-y-1.5" data-testid="family-invite-result">
          <p className="text-xs font-semibold">
            One-time code for {inviteResult.email} (7 days{inviteResult.email_sent ? ", also emailed" : " — email couldn't be sent, share it yourself"}):
          </p>
          <div className="flex items-center gap-2">
            <code className="text-xs font-bold bg-card rounded-lg px-2 py-1.5 flex-1 break-all" data-testid="family-invite-code">{inviteResult.code}</code>
            <Button variant="outline" size="sm" className="rounded-lg shrink-0 h-8"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(inviteResult.code);
                  toast.success("Code copied");
                } catch {
                  toast.error("Couldn't copy automatically — select the code and copy it manually");
                }
              }}
              data-testid="family-invite-copy">
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      )}
      {data.members.length === 1 && (
        <form onSubmit={join} className="space-y-1.5 border-t border-border pt-3">
          <p className="text-xs font-semibold text-muted-foreground">Invited to someone's family? Enter the code:</p>
          <div className="flex gap-2">
            <Input required placeholder="Invite code" value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)} className="h-9 text-sm" data-testid="family-join-input" />
            <Button type="submit" disabled={busy} size="sm" variant="outline" className="rounded-lg shrink-0 h-9" data-testid="family-join-button">
              Join
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
